import {DigitizationTools} from "./ToolRegistry.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {createFeature} from "./Feature.js";

import {T} from "./i18n/index.js";
// Afvigelsesanalysen (farvelægning af punktskyen efter afstanden til et Snits plan) som ÉT objekt under Målinger i Registreringer.
// Selve farvelægningen ejes af værten (Snit-panelets afvigelsesfane); denne bro giver den blot en række i træet:
//
//   træets øje     ->  info.setVisible (farverne skjules/vises)
//   træets slet    ->  "deviation_removed" på modulet (værten nulstiller farverne)
//   nulstilling    ->  removeNativeDeviation (rækken fjernes uden at sende "deviation_removed")
//   dobbeltklik    ->  "open_deviation_requested" (værten åbner Snit-dialogen på fanen Afvigelse)
//
// Der findes højst én række (der er kun én farvelægning ad gangen); en ny farvelægning opdaterer den.

export const DEVIATION_TOOL_ID = "digitize_deviation";

export const DeviationTool = {
	id: DEVIATION_TOOL_ID,
	name: T("Afvigelsesanalyse"),
	category: "hidden",
	kind: "measurement",
	icon: "clip_volume.svg",
	resultGeometryType: "Point",
	featureType: T("Afvigelsesanalyse"),
	tooltip: T("Afvigelsesanalyse (punktskyen farvelagt efter afstanden til et Snits plan)."),
	customCapture(){
		// Intet: afvigelsesanalysen oprettes af Snit-panelet, ikke af et tegneværktøj.
	},
};

DigitizationTools.register(DeviationTool);

// Tegner intet selv (farvelægningen er selve punktskyen) - bærer kun træ-rækkens tilstand.
class NativeDeviationVisual extends FeatureVisual{
	constructor(info){
		super("Point");
		this.editable = false;
		this.userData.nativeDeviation = info;
	}

	rawCoordinates(){
		const c = this.userData.nativeDeviation.center || [0, 0, 0];
		return [c.slice()];
	}

	toCoordinates(){
		return this.rawCoordinates()[0];
	}
}

function findVisual(dm){
	for(const [featureId, visual] of dm.visuals){
		if(visual.userData.nativeDeviation) return {featureId, visual};
	}
	return null;
}

// info: {name, snitId, center: [x, y, z], getVisible(), setVisible(bool)}. Idempotent: findes rækken, opdateres den. Returnerer feature-id'et.
export function registerNativeDeviation(dm, info){
	const existing = findVisual(dm);
	if(existing){
		Object.assign(existing.visual.userData.nativeDeviation, info);
		if(info.name) dm.renameFeature(existing.featureId, info.name);
		return existing.featureId;
	}

	const visual = new NativeDeviationVisual(Object.assign({}, info));
	dm.scene.add(visual);

	const feature = createFeature({
		geometryType: "Point",
		coordinates: visual.toCoordinates(),
		category: DeviationTool.category,
		tool: DEVIATION_TOOL_ID,
		featureType: DeviationTool.featureType,
		name: info.name,
		attributes: {snitId: info.snitId},
	});
	visual.setInfo(feature.properties.name, feature.properties.description);

	// Træets øje styrer, om farvelægningen vises.
	Object.defineProperty(visual, "visible", {
		configurable: true,
		get: () => { const i = visual.userData.nativeDeviation; return i.getVisible ? i.getVisible() !== false : true; },
		set: (value) => { const i = visual.userData.nativeDeviation; if(i.setVisible) i.setVisible(!!value); },
	});

	dm._wireFeatureLifecycle(feature, visual);
	dm.removeFeatureSnapTargets(feature.id);

	const onFeatureRemoved = (e) => {
		if(!e.feature || e.feature.id !== feature.id) return;
		dm.featureManager.removeEventListener("feature_removed", onFeatureRemoved);
		if(!visual.userData.releasing) dm.dispatchEvent({type: "deviation_removed", info: visual.userData.nativeDeviation});
	};
	dm.featureManager.addEventListener("feature_removed", onFeatureRemoved);

	dm.dispatchEvent({type: "feature_created", feature, visual});
	return feature.id;
}

// Fjerner rækken (efter en nulstilling) uden at melde det som en sletning i træet.
export function removeNativeDeviation(dm){
	const existing = findVisual(dm);
	if(!existing) return false;
	existing.visual.userData.releasing = true;
	dm.removeFeature(existing.featureId);
	return true;
}

// info bag en feature (null for alle andre registreringer).
export function getNativeDeviation(dm, featureId){
	const visual = dm.getVisual(featureId);
	return visual && visual.userData.nativeDeviation ? visual.userData.nativeDeviation : null;
}
